import Decimal from 'decimal.js';

/**
 * All monetary math goes through Decimal. Floats are only accepted at the
 * edges (Deriv JSON responses) and converted immediately via `money()`.
 */
Decimal.set({ precision: 40, rounding: Decimal.ROUND_HALF_EVEN });

export type MoneyInput = Decimal.Value | { toString(): string };
export { Decimal };

export function money(value: MoneyInput | null | undefined): Decimal {
  if (value === null || value === undefined || value === '') return new Decimal(0);
  if (value instanceof Decimal) {
    if (!value.isFinite()) throw new Error('Invalid monetary value');
    return value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error(`Invalid monetary value: ${value}`);
    // Use the shortest round-trip representation to avoid binary artefacts.
    return new Decimal(value.toString());
  }
  const result = new Decimal(value.toString());
  if (!result.isFinite()) throw new Error('Invalid monetary value');
  return result;
}

export function sum(values: Iterable<MoneyInput>): Decimal {
  let total = new Decimal(0);
  for (const v of values) total = total.plus(money(v));
  return total;
}

/** Decimal places used by Deriv for a given account currency. */
export function currencyDecimals(currency: string): number {
  const c = currency.toUpperCase();
  if (['BTC', 'ETH', 'LTC', 'USDC', 'UST', 'EUSDT', 'TUSDT'].includes(c)) return 8;
  return 2;
}

/** Round to the currency's precision (Deriv rejects extra decimals). */
export function roundMoney(value: MoneyInput, currency = 'USD'): Decimal {
  return money(value).toDecimalPlaces(currencyDecimals(currency), Decimal.ROUND_DOWN);
}

/** Serialise for transport/storage. Always a string, never a float. */
export function toMoneyString(value: MoneyInput, currency = 'USD'): string {
  return money(value).toFixed(currencyDecimals(currency));
}

/** Convert to number ONLY for Deriv request payloads, after rounding. */
export function toBrokerNumber(value: MoneyInput, currency = 'USD'): number {
  return Number(roundMoney(value, currency).toFixed(currencyDecimals(currency)));
}

/** Return on stake as a percentage, e.g. stake 10 payout 19.53 => 95.30 */
export function returnPercent(stake: MoneyInput, payout: MoneyInput): Decimal {
  const s = money(stake);
  if (s.lte(0)) return new Decimal(0);
  return money(payout).minus(s).div(s).times(100).toDecimalPlaces(2);
}

export function potentialProfit(stake: MoneyInput, payout: MoneyInput): Decimal {
  return money(payout).minus(money(stake));
}
